import React, {Dispatch, SetStateAction} from "react";
import {
    AllStates, asJsonformsErrorTranslate, asJsonformsTranslate,
    JSON_FORMS_CONFIG,
    Level, NON_JSON_ERROR_PREFIX,
    StepForm,
    TranslateFunction
} from '../Forms';

import schema from './modules.schema.json';

import {JSONSchema7} from "json-schema";
import {JsonForms} from "@jsonforms/react";
import uischema from "./modules.uischema.json";
import {materialCells, materialRenderers} from "@jsonforms/material-renderers";
import {ModuleList} from "./Modules";
import {Paper, Typography} from "@mui/material";
import * as DegreesType from "../degrees/Degrees";
import {DegreeProgram, getDegreeProgramsArray, getProgramTitle} from "../degrees/Degrees";
import Ajv, {ErrorObject, Schema} from "ajv";
import {I18nMessage} from "../Representation";
import addFormats from "ajv-formats";
import Stack from "@mui/material/Stack";
import {createTheme} from "@mui/material/styles";
import ErrorMessageArrayLayoutRenderer, {materialArrayControlTester} from "../ErrorMessageArrayControlRenderer";
import {ArrayErrorMessagesContext} from "../Wizard";

export const EMPTY_MODULES: ModuleList[] = [];

// export const ArrayErrorMessagesContext = createContext<string[]>([]);

const FORM_NS = "modules_form.";

function roundCreditsToHalf(moduleList: ModuleList) {
    moduleList.modules.forEach(module => {
        if (module.credits)
            module.credits = Math.round(module.credits * 2) / 2;
    })
}

export class ModulesForm extends StepForm {

    constructor() {
        super(schema as JSONSchema7);
    }

    getFormNs(): string | undefined {
        return FORM_NS;
    }

    getTitleKey() {
        return "modules_title";
    };

    getPage([formData, setFormData]: [any, Dispatch<SetStateAction<any>>],  { degrees: [degrees, ] }: AllStates, t: TranslateFunction, lang: string) {
        const modules = (formData as unknown) as ModuleList[];

        if (!modules)
            return <React.Fragment/>;

        const programs: DegreesType.DegreeProgram[] = getDegreeProgramsArray(degrees);

        // ensure that modules has a ModuleList for each degree program.
        if (modules.length < programs.length) {
            for (let i = modules.length; i < programs.length; i++) {
                modules.push({
                    degree_program_index: i,
                    modules: []
                });
            }
        }

        const ajv = new Ajv({
            allErrors: true,
            verbose: true,
            strict: false
        })
        addFormats(ajv);

        createTheme()

        return (<React.Fragment>

            {
                programs.map((program, programIndex) => {
                    schema.properties.modules.items.properties.title.title = t(FORM_NS + "modules.title.label");
                    schema.properties.modules.items.properties.credits.title = t(FORM_NS + "modules.credits.label");
                    schema.properties.modules.items.properties.module_url.title = t(FORM_NS + "modules.module_url.label");

                    ajv.validate(schema, modules[programIndex]);
                    const extraErrors = this.validateExtra(modules[programIndex], t, programIndex, program);
                    const ajvErrors = ajv.errors ?? [];
                    const allErrors = [...ajvErrors, ...extraErrors];
                    const allErrorsTranslated = allErrors.map(error => ({
                        ...error,
                        message: t("ajv_error." + error.keyword, error.message, error.params)
                    }));
                    const arrayPath = "/modules"

                    const modulesErrors = allErrors.filter(error => error.instancePath.startsWith(arrayPath));
                    const modulesErrorMessages : string[] = [];
                    modulesErrors
                        .filter(error => error.instancePath === arrayPath)
                        .forEach(error => modulesErrorMessages.push(error.message ?? t("there_are_errors")));
                    if (modulesErrors.find(error => error.instancePath.length > arrayPath.length)) {
                        modulesErrorMessages.push(t("error_in_module_detail"));
                    }

                    const renderers = [
                        ...materialRenderers,
                        //register custom renderers
                        { tester: materialArrayControlTester, renderer: ErrorMessageArrayLayoutRenderer },
                    ];

                    return <Paper elevation={0} variant="outlined" sx={{padding: 2}} key={getProgramTitle(program)}>
                        <Stack spacing={2}>
                            <Typography variant="h5">{getProgramTitle(programs[programIndex])}</Typography>
                            <ArrayErrorMessagesContext.Provider value={modulesErrorMessages}>
                                <JsonForms
                                    schema={schema}
                                    uischema={uischema}
                                    data={modules[programIndex]}
                                    renderers={renderers}
                                    cells={materialCells}
                                    config={JSON_FORMS_CONFIG}
                                    i18n={{
                                        translate: asJsonformsTranslate(t, FORM_NS),
                                        locale: lang,
                                        translateError: asJsonformsErrorTranslate(t)
                                    }}
                                    validationMode={"NoValidation"}
                                    additionalErrors={allErrorsTranslated}
                                    onChange={({data}) => {
                                        roundCreditsToHalf(data);
                                        setFormData(modules.map((ml, currentIndex) => programIndex === currentIndex ? data : ml));
                                    }}
                                />
                            </ArrayErrorMessagesContext.Provider>
                        </Stack>
                    </Paper>
                })
            }

        </React.Fragment>);
    }

    validate(newAjv: () => Ajv, data: any, t: TranslateFunction, {degrees: [degrees, ]} : AllStates): ErrorObject[] {

        const programs: DegreesType.DegreeProgram[] = getDegreeProgramsArray(degrees);

        const moduleLists = ((data as unknown) as ModuleList[]);
        if (moduleLists === undefined) {
            return [];
        }
        const errors: ErrorObject[] = [];

        moduleLists.forEach((moduleList, index) => {
            const ajv = newAjv();
            const valid = ajv.validate(this._schema as Schema, moduleList)
            if (!valid) {
                errors.push(...(ajv.errors ?? []));
            }

            errors.push(...this.validateExtra(moduleList, t, index, programs[index]));
        });

        if (moduleLists.length < programs.length) {
            const keyword = "incomplete";
            const params = {
                moduleListIndex: programs.length - 1,
                programTitle: getProgramTitle(programs[programs.length - 1]),
                requiredCredits: programs[programs.length - 1].total_credits,
                actualCredits: 0
            };
            const defaultMsg = "must enter all modules for all programs";
            errors.push({
                keyword: keyword,
                instancePath: "/modules",
                schemaPath: "#/properties/modules/incomplete",
                params: params,
                message: t("ajv_error." + keyword, defaultMsg, params)
            });
        }
        return errors;
    }

    validateExtra(moduleList: ModuleList, t: TranslateFunction, index: number, program: DegreeProgram): ErrorObject[] {
        const errors: ErrorObject[] = [];

        const credits = moduleList.modules.map(mod => mod.credits).reduce((acc, curr) => (acc + curr), 0);
        // for qualifying degree at least 80% of the credits must be completed
        if (index === 0 && program && program.total_credits && credits < (program.total_credits * 0.8)) {
            const keyword = NON_JSON_ERROR_PREFIX + "incomplete";
            const params = {
                moduleListIndex: index,
                programTitle: getProgramTitle(program),
                requiredCredits: program.total_credits,
                actualCredits: credits
            };
            const defaultMsg = "must enter enough modules";
            errors.push({
                keyword: keyword,
                instancePath: "/modules",
                schemaPath: "#/properties/modules/incomplete",
                params: params,
                message: t("ajv_error." + keyword, defaultMsg, params)
            });
        } else if (program && program.total_credits && credits > program.total_credits) {
                const keyword = NON_JSON_ERROR_PREFIX + "inconsistent";
                const params = {moduleListIndex: index, programTitle: getProgramTitle(program), requiredCredits: program.total_credits, actualCredits: credits};
                const defaultMsg = "must not enter too many modules";
                errors.push({
                    keyword: keyword,
                    instancePath: "/modules",
                    schemaPath: "#/properties/modules/inconsistent",
                    params: params,
                    message: t("ajv_error." + keyword, defaultMsg, params)
                });
        } else if (program && !program.total_credits) {
            const keyword = "degreeDataIncomplete";
            const params = {moduleListIndex: index, programTitle: getProgramTitle(program) };
            const defaultMsg = "fully specify information for degree program";
            errors.push({
                keyword: keyword,
                instancePath: "/modules",
                schemaPath: "#/properties/modules/incomplete",
                params: params,
                message: t("ajv_error." + keyword, defaultMsg, params)
            });
        }

        const seenModuleTitles = new Map<string, { instancePath: string, schemaPath: string }[]>();
        moduleList.modules.forEach((module, i) => {
            const paths = seenModuleTitles.get(module.title) ?? [];
            paths.push({
                instancePath: "/modules/" + i + "/title",
                schemaPath: "#/properties/modules/items/properties/title/duplicate"
            })
            // "/modules/12/credits"
            // "#/properties/modules/items/properties/credits/minimum"
            seenModuleTitles.set(module.title, paths);
        })
        const primaryKey = t(FORM_NS + "modules.title.label");
        Array.from(seenModuleTitles.values()).forEach(paths => {
            if (paths && paths.length > 1) {
                paths.forEach(path => {
                    const keyword = "notUnique";
                    const params = {primaryKey: primaryKey, moduleListIndex: index};
                    const defaultMsg = "must be unique";
                    errors.push(
                        {
                            keyword: keyword,
                            instancePath: path.instancePath,
                            schemaPath: path.schemaPath,
                            params: params,
                            message: t("ajv_error." + keyword, defaultMsg, params)
                        })

                });
            }
        });

        moduleList.modules.forEach((module, i) => {
            if (module.credits !== Math.round(module.credits * 2) / 2) {
                const keyword = "invalid";
                const params = {
                    actualValue: module.credits ? module.credits.toString() : "",
                    nearestLegalValue: module.credits ? (Math.round(module.credits * 2) / 2).toString() : ""
                };
                const defaultMsg = "only whole numbers and halt numbers allowed. {actualValue} {nearestLegalValue}";
                errors.push({
                    keyword: keyword,
                    instancePath: "/modules/" + i + "/credits",
                    schemaPath: "#/properties/modules/items/properties/credits/incomplete",
                    params: params,
                    message: t("ajv_error." + keyword, defaultMsg, params)
                });
            }
        })

        return errors;
    }

    getContextMessageKeys = (_data: any, _allStates: AllStates): [I18nMessage[], I18nMessage[]] => {
        return [[], []];
    }

    getValidationMessageKeys(data: any, allStates: AllStates, t: TranslateFunction): null | {
        level: Level;
        messages: any[]
    } {
        const modules = ((data as unknown) as ModuleList[]);

        const programs: DegreesType.DegreeProgram[] = getDegreeProgramsArray(allStates.degrees[0]);

        if (modules === undefined ||
            modules
                .filter(moduleList => moduleList.degree_program_index < programs.length)
                .flatMap(moduleList => moduleList.modules).length === 0) {
            return {
                level: Level.INCOMPLETE,
                messages: ["no_modules"]
            };
        }

        return super.getValidationMessageKeys(data, allStates, t);
    }

    getExplanationMarkdownKey() {
        return "modules.explanation.markdown";
    }

    getHelpUrl(): string {
        return "faq.html#modules";
    }

}